import React, { useEffect, useMemo, useState } from 'react'
import { AppstoreOutlined, ShoppingCartOutlined } from '@ant-design/icons'
import { Alert, Card, Col, DatePicker, Row, Space, Spin, Statistic, Tag, Typography } from 'antd'
import dayjs, { type Dayjs } from 'dayjs'
import { sellerDashboardAPI } from '../utils/sellerApi'
import { formatSellerCurrency, useSellerCurrencyStore } from '../utils/currency'

type UnknownRecord = Record<string, unknown>

type DashboardResponse = {
  totals?: {
    total_products?: number
    total_approved_products?: number
    total_orders?: number
    total_revenue?: number
    total_employees?: number
    wallet_total_earnings?: number
    wallet_total_payout_requested?: number
  }
  analytics?: {
    revenue_chart?: ChartPayload
    orders_chart?: ChartPayload
    order_status_pie?: ChartPayload
    inventory_bar?: ChartPayload
    weekday_orders?: ChartPayload
    top_products?: ChartPayload
    order_value_buckets?: ChartPayload
    category_sales?: ChartPayload
    revenue_trend?: ChartPayload
    personalized_insights?: Array<{
      type?: string
      title?: string
      message?: string
    }>
  }
  analysis?: {
    period_comparison?: {
      range_days?: number
      current_revenue?: number
      previous_revenue?: number
      revenue_change_percent?: number
      current_orders?: number
      previous_orders?: number
      orders_change_percent?: number
      current_aov?: number
      previous_aov?: number
      aov_change_percent?: number
    }
    weekday_analysis?: {
      peak_day?: string | null
      peak_orders?: number
      weekday_total_orders?: number
      peak_share_percent?: number
    }
    top_product_analysis?: {
      top_rows?: Array<{
        label: string
        units_sold: number
        revenue: number
      }>
      top_product_share?: number
      top_product_name?: string | null
      top_product_units?: number
      top_product_revenue?: number
    }
    order_value_analysis?: {
      rows?: Array<{
        label: string
        orders: number
      }>
      premium_order_share?: number
    }
    inventory_analysis?: {
      status?: 'healthy' | 'watch' | 'critical'
      low_stock_products?: number
      total_products?: number
      low_stock_ratio?: number
      product_limit_usage_percent?: number
    }
    plan_utilization?: {
      product_limit?: number | null
      staff_limit?: number | null
      product_usage_percent?: number
      staff_usage_percent?: number
    }
    recommendations?: Array<{
      severity?: 'success' | 'info' | 'warning' | 'error'
      title?: string
      message?: string
    }>
  }
  analytics_access?: {
    level?: 'basic' | 'advanced' | 'personalized'
    advanced?: boolean
    personalized?: boolean
  }
  entitlements?: {
    product_limit?: number | null
    staff_limit?: number | null
    product_limit_increment?: number
    staff_limit_increment?: number
  }
  feature_roadmap?: {
    ai_features?: Array<{
      code: string
      name: string
      status: string
      description?: string
      source_add_on?: string | null
      group?: string | null
    }>
  }
}

type ChartPayload = {
  labels?: Array<string | number>
  datasets?: Array<{
    label?: string
    data?: Array<number | string | null>
  }>
  data?: unknown
  values?: unknown
  series?: unknown
}

type ChartRow = {
  label: string
  value: number
}

type TrendPoint = ChartRow & {
  x: number
  y: number
}

const CHART_BOX_STYLE: React.CSSProperties = {
  width: '100%',
  height: 280,
  minWidth: 0,
}

const SVG_WIDTH = 920
const SVG_HEIGHT = 280
const SVG_PADDING = {
  top: 20,
  right: 24,
  bottom: 44,
  left: 46,
}

const AXIS_COLOR = '#d7e2df'
const GRID_COLOR = '#edf3f2'
const TEXT_COLOR = '#6b7d79'

const CHART_COLORS = ['#0f766e', '#0ea5e9', '#f59e0b', '#9333ea', '#ef4444']
const ANALYSIS_THEME = {
  comparison: { border: '#38bdf8', background: 'linear-gradient(180deg, rgba(56, 189, 248, 0.12) 0%, #ffffff 55%)', tag: 'blue' as const },
  signals: { border: '#f59e0b', background: 'linear-gradient(180deg, rgba(245, 158, 11, 0.12) 0%, #ffffff 55%)', tag: 'orange' as const },
  recommendations: { border: '#a855f7', background: 'linear-gradient(180deg, rgba(168, 85, 247, 0.12) 0%, #ffffff 55%)', tag: 'purple' as const },
  revenueTrend: { border: '#0f766e', background: 'linear-gradient(180deg, rgba(15, 118, 110, 0.12) 0%, #ffffff 55%)', tag: 'green' as const },
  topProducts: { border: '#6366f1', background: 'linear-gradient(180deg, rgba(99, 102, 241, 0.12) 0%, #ffffff 55%)', tag: 'geekblue' as const },
  categorySales: { border: '#ec4899', background: 'linear-gradient(180deg, rgba(236, 72, 153, 0.12) 0%, #ffffff 55%)', tag: 'magenta' as const },
} as const

const RESERVED_CHART_KEYS = new Set(['labels', 'datasets', 'data', 'values', 'series', 'label'])
const TOOLTIP_WIDTH = 156
const TOOLTIP_HEIGHT = 58

const clamp = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), max)

const shortenLabel = (value: string, maxLength = 14) => {
  const label = value.trim()
  return label.length > maxLength ? `${label.slice(0, maxLength - 2)}..` : label
}

const isObject = (value: unknown): value is UnknownRecord =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

const normalizeNumber = (value: unknown): number => {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : 0
}

const normalizeLabel = (value: unknown, fallback: string): string => {
  if (typeof value === 'string' && value.trim().length > 0) {
    return value
  }

  if (typeof value === 'number') {
    return String(value)
  }

  return fallback
}

const prettifyKey = (value: string) =>
  value
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase())

const extractRowsFromObjectArray = (rows: UnknownRecord[]): ChartRow[] =>
  rows.map((row, index) => ({
    label: normalizeLabel(
      row.label ?? row.name ?? row.date ?? row.day ?? row.status,
      `Item ${index + 1}`,
    ),
    value: normalizeNumber(
      row.value ??
        row.total ??
        row.count ??
        row.amount ??
        row.revenue ??
        row.orders ??
        row.stock,
    ),
  }))

const normalizeSeriesChart = (chart?: ChartPayload, datasetIndex = 0): ChartRow[] => {
  if (!chart || !isObject(chart)) {
    return []
  }

  const labels = Array.isArray(chart.labels) ? chart.labels : []
  const datasetValues = Array.isArray(chart.datasets?.[datasetIndex]?.data)
    ? chart.datasets?.[datasetIndex]?.data ?? []
    : null

  if (labels.length > 0 && datasetValues) {
    return labels.map((label, index) => ({
      label: normalizeLabel(label, `Item ${index + 1}`),
      value: normalizeNumber(datasetValues[index]),
    }))
  }

  if (labels.length > 0 && Array.isArray(chart.data)) {
    return labels.map((label, index) => ({
      label: normalizeLabel(label, `Item ${index + 1}`),
      value: normalizeNumber(chart.data[index]),
    }))
  }

  if (labels.length > 0 && isObject(chart.data)) {
    return labels.map((label, index) => {
      const labelKey = normalizeLabel(label, `Item ${index + 1}`)
      return {
        label: labelKey,
        value: normalizeNumber((chart.data as UnknownRecord)[labelKey]),
      }
    })
  }

  if (Array.isArray(chart.data)) {
    if (
      chart.data.every((entry) => typeof entry === 'number' || typeof entry === 'string' || entry === null)
    ) {
      return chart.data.map((entry, index) => ({
        label: `Item ${index + 1}`,
        value: normalizeNumber(entry),
      }))
    }

    if (chart.data.every((entry) => isObject(entry))) {
      return extractRowsFromObjectArray(chart.data as UnknownRecord[])
    }
  }

  if (Array.isArray(chart.values)) {
    return chart.values.map((entry, index) => ({
      label: `Item ${index + 1}`,
      value: normalizeNumber(entry),
    }))
  }

  if (Array.isArray(chart.series) && chart.series.every((entry) => isObject(entry))) {
    return extractRowsFromObjectArray(chart.series as UnknownRecord[])
  }

  const objectEntries = Object.entries(chart).filter(
    ([key, value]) =>
      !RESERVED_CHART_KEYS.has(key) && (typeof value === 'number' || typeof value === 'string'),
  )

  if (objectEntries.length > 0) {
    return objectEntries.map(([key, value]) => ({
      label: prettifyKey(key),
      value: normalizeNumber(value),
    }))
  }

  return []
}

const formatNumber = (value: number) => Number(value ?? 0).toLocaleString()
const formatCurrency = (value: number) =>
  formatSellerCurrency(Number(value ?? 0), { fromInr: true })

const formatPercent = (value?: number | null) => {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return '0.0%'
  }

  const prefix = value > 0 ? '+' : ''
  return `${prefix}${value.toFixed(1)}%`
}

const sumSeriesValues = (rows: ChartRow[]) =>
  rows.reduce((sum, row) => sum + normalizeNumber(row.value), 0)

const ensureSeriesHasVisibleSignal = (
  rows: ChartRow[],
  total: number,
  fallbackLabel: string,
): ChartRow[] => {
  if (rows.length === 0 && total > 0) {
    return [{ label: fallbackLabel, value: total }]
  }

  if (rows.length > 0 && sumSeriesValues(rows) <= 0 && total > 0) {
    return rows.map((row, index) => ({
      ...row,
      value: index === rows.length - 1 ? total : 0,
    }))
  }

  return rows
}

const ensurePieHasVisibleSignal = (rows: ChartRow[], total: number): ChartRow[] => {
  if (rows.length === 0 && total > 0) {
    return [{ label: 'Orders', value: total }]
  }

  if (rows.length > 0 && sumSeriesValues(rows) <= 0 && total > 0) {
    return rows.map((row, index) => ({
      ...row,
      value: index === 0 ? total : 0,
    }))
  }

  return rows
}

const toChartPoints = (
  rows: ChartRow[],
): { points: TrendPoint[]; baselineY: number; maxValue: number } => {
  const chartWidth = SVG_WIDTH - SVG_PADDING.left - SVG_PADDING.right
  const chartHeight = SVG_HEIGHT - SVG_PADDING.top - SVG_PADDING.bottom
  const baselineY = SVG_HEIGHT - SVG_PADDING.bottom
  const maxValue = Math.max(...rows.map((row) => row.value), 1)
  const step = rows.length > 1 ? chartWidth / (rows.length - 1) : 0

  const points = rows.map((row, index) => {
    const x = SVG_PADDING.left + step * index
    const y = baselineY - (row.value / maxValue) * chartHeight
    return {
      ...row,
      x,
      y,
    }
  })

  return { points, baselineY, maxValue }
}

const tickIndexes = (length: number): number[] => {
  if (length <= 0) {
    return []
  }

  if (length <= 6) {
    return Array.from({ length }, (_, index) => index)
  }

  const values = [0, Math.floor((length - 1) / 2), length - 1]
  return Array.from(new Set(values))
}

const ChartEmptyState = ({ message }: { message: string }) => (
  <div
    style={{
      height: '100%',
      display: 'grid',
      placeItems: 'center',
      color: TEXT_COLOR,
      fontSize: 13,
    }}
  >
    {message}
  </div>
)

const TrendChart = ({
  rows,
  stroke,
  fill,
  valueFormatter,
}: {
  rows: ChartRow[]
  stroke: string
  fill?: string
  valueFormatter: (value: number) => string
}) => {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null)

  if (rows.length === 0) {
    return <ChartEmptyState message="No trend data available" />
  }

  const { points, baselineY, maxValue } = toChartPoints(rows)
  const xTicks = tickIndexes(points.length)
  const hoveredPoint = hoveredIndex !== null ? points[hoveredIndex] : null
  const linePath = points
    .map((point, index) => `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`)
    .join(' ')

  const areaPath =
    points.length > 1
      ? `${linePath} L ${points[points.length - 1].x} ${baselineY} L ${points[0].x} ${baselineY} Z`
      : ''

  return (
    <svg
      viewBox={`0 0 ${SVG_WIDTH} ${SVG_HEIGHT}`}
      width="100%"
      height="100%"
      role="img"
      aria-label="Trend chart"
    >
      <line
        x1={SVG_PADDING.left}
        y1={baselineY}
        x2={SVG_WIDTH - SVG_PADDING.right}
        y2={baselineY}
        stroke={AXIS_COLOR}
        strokeWidth={1}
      />
      <line
        x1={SVG_PADDING.left}
        y1={SVG_PADDING.top}
        x2={SVG_PADDING.left}
        y2={baselineY}
        stroke={AXIS_COLOR}
        strokeWidth={1}
      />

      {[0.25, 0.5, 0.75, 1].map((ratio, index) => {
        const y = baselineY - ratio * (baselineY - SVG_PADDING.top)
        const value = maxValue * ratio
        return (
          <g key={`grid-${index}`}>
            <line
              x1={SVG_PADDING.left}
              y1={y}
              x2={SVG_WIDTH - SVG_PADDING.right}
              y2={y}
              stroke={GRID_COLOR}
              strokeWidth={1}
              strokeDasharray="4 4"
            />
            <text
              x={SVG_PADDING.left - 8}
              y={y + 4}
              textAnchor="end"
              fill={TEXT_COLOR}
              fontSize={11}
            >
              {valueFormatter(value)}
            </text>
          </g>
        )
      })}

      {fill && areaPath ? <path d={areaPath} fill={fill} /> : null}

      {points.length > 1 ? (
        <path
          d={linePath}
          fill="none"
          stroke={stroke}
          strokeWidth={3}
          strokeLinejoin="round"
          strokeLinecap="round"
        />
      ) : null}

      {points.length === 1 ? (
        <line
          x1={points[0].x}
          y1={baselineY}
          x2={points[0].x}
          y2={points[0].y}
          stroke={stroke}
          strokeWidth={4}
          strokeLinecap="round"
        />
      ) : null}

      {points.map((point, index) => (
        <circle
          key={`${point.label}-${index}`}
          cx={point.x}
          cy={point.y}
          r={hoveredIndex === index ? 6 : 3.6}
          fill={stroke}
          stroke="#ffffff"
          strokeWidth={hoveredIndex === index ? 3 : 0}
          style={{ transition: 'r 140ms ease, stroke-width 140ms ease' }}
        />
      ))}

      {xTicks.map((index) => (
        <text
          key={`tick-${index}`}
          x={points[index].x}
          y={SVG_HEIGHT - 16}
          textAnchor="middle"
          fill={TEXT_COLOR}
          fontSize={11}
        >
          {points[index].label}
        </text>
      ))}

      {points.map((point, index) => {
        const hitWidth = points.length > 1 ? Math.max(24, (SVG_WIDTH - SVG_PADDING.left - SVG_PADDING.right) / points.length) : 160

        return (
          <rect
            key={`hit-${point.label}-${index}`}
            x={point.x - hitWidth / 2}
            y={SVG_PADDING.top}
            width={hitWidth}
            height={baselineY - SVG_PADDING.top}
            fill="transparent"
            tabIndex={0}
            role="button"
            aria-label={`${point.label}: ${valueFormatter(point.value)}`}
            onMouseEnter={() => setHoveredIndex(index)}
            onMouseLeave={() => setHoveredIndex(null)}
            onFocus={() => setHoveredIndex(index)}
            onBlur={() => setHoveredIndex(null)}
          />
        )
      })}

      {hoveredPoint ? (
        <g pointerEvents="none">
          <line
            x1={hoveredPoint.x}
            y1={SVG_PADDING.top}
            x2={hoveredPoint.x}
            y2={baselineY}
            stroke={stroke}
            strokeWidth={1}
            strokeDasharray="4 4"
            opacity={0.45}
          />
          <rect
            x={clamp(hoveredPoint.x - TOOLTIP_WIDTH / 2, SVG_PADDING.left, SVG_WIDTH - SVG_PADDING.right - TOOLTIP_WIDTH)}
            y={clamp(hoveredPoint.y - TOOLTIP_HEIGHT - 14, 8, baselineY - TOOLTIP_HEIGHT - 8)}
            width={TOOLTIP_WIDTH}
            height={TOOLTIP_HEIGHT}
            rx={8}
            fill="#ffffff"
            stroke="#dfe8e4"
            filter="drop-shadow(0 10px 18px rgba(15, 32, 26, 0.16))"
          />
          <text
            x={clamp(hoveredPoint.x - TOOLTIP_WIDTH / 2, SVG_PADDING.left, SVG_WIDTH - SVG_PADDING.right - TOOLTIP_WIDTH) + 12}
            y={clamp(hoveredPoint.y - TOOLTIP_HEIGHT - 14, 8, baselineY - TOOLTIP_HEIGHT - 8) + 22}
            fill={TEXT_COLOR}
            fontSize={11}
            fontWeight={700}
          >
            {shortenLabel(hoveredPoint.label, 18)}
          </text>
          <text
            x={clamp(hoveredPoint.x - TOOLTIP_WIDTH / 2, SVG_PADDING.left, SVG_WIDTH - SVG_PADDING.right - TOOLTIP_WIDTH) + 12}
            y={clamp(hoveredPoint.y - TOOLTIP_HEIGHT - 14, 8, baselineY - TOOLTIP_HEIGHT - 8) + 43}
            fill="#12211b"
            fontSize={14}
            fontWeight={800}
          >
            {valueFormatter(hoveredPoint.value)}
          </text>
        </g>
      ) : null}
    </svg>
  )
}

const BarDistributionChart = ({
  rows,
  color,
  valueFormatter = formatNumber,
}: {
  rows: ChartRow[]
  color: string
  valueFormatter?: (value: number) => string
}) => {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null)

  if (rows.length === 0) {
    return <ChartEmptyState message="No inventory data available" />
  }

  const baselineY = SVG_HEIGHT - SVG_PADDING.bottom
  const maxValue = Math.max(...rows.map((row) => row.value), 1)
  const chartWidth = SVG_WIDTH - SVG_PADDING.left - SVG_PADDING.right
  const barSlot = chartWidth / rows.length
  const barWidth = Math.min(64, barSlot * 0.58)
  const chartHeight = baselineY - SVG_PADDING.top

  return (
    <svg
      viewBox={`0 0 ${SVG_WIDTH} ${SVG_HEIGHT}`}
      width="100%"
      height="100%"
      role="img"
      aria-label="Bar distribution chart"
    >
      <line
        x1={SVG_PADDING.left}
        y1={baselineY}
        x2={SVG_WIDTH - SVG_PADDING.right}
        y2={baselineY}
        stroke={AXIS_COLOR}
        strokeWidth={1}
      />
      <line
        x1={SVG_PADDING.left}
        y1={SVG_PADDING.top}
        x2={SVG_PADDING.left}
        y2={baselineY}
        stroke={AXIS_COLOR}
        strokeWidth={1}
      />

      {rows.map((row, index) => {
        const xCenter = SVG_PADDING.left + barSlot * index + barSlot / 2
        const barHeight = (row.value / maxValue) * chartHeight
        const x = xCenter - barWidth / 2
        const y = baselineY - barHeight

        return (
          <g key={`${row.label}-${index}`}>
            <rect
              x={x}
              y={y}
              width={barWidth}
              height={Math.max(barHeight, 2)}
              rx={8}
              fill={color}
              opacity={hoveredIndex === null || hoveredIndex === index ? 0.92 : 0.38}
              style={{ transition: 'opacity 140ms ease' }}
              onMouseEnter={() => setHoveredIndex(index)}
              onMouseLeave={() => setHoveredIndex(null)}
              onFocus={() => setHoveredIndex(index)}
              onBlur={() => setHoveredIndex(null)}
              tabIndex={0}
              role="button"
              aria-label={`${row.label}: ${formatNumber(row.value)}`}
            />
            <text x={xCenter} y={y - 8} textAnchor="middle" fill={hoveredIndex === index ? '#12211b' : TEXT_COLOR} fontSize={11} fontWeight={hoveredIndex === index ? 800 : 400}>
              {valueFormatter(row.value)}
            </text>
            <text
              x={xCenter}
              y={SVG_HEIGHT - 16}
              textAnchor="middle"
              fill={TEXT_COLOR}
              fontSize={11}
            >
              {shortenLabel(row.label)}
            </text>
          </g>
        )
      })}

      {hoveredIndex !== null ? (() => {
        const row = rows[hoveredIndex]
        const xCenter = SVG_PADDING.left + barSlot * hoveredIndex + barSlot / 2
        const barHeight = (row.value / maxValue) * chartHeight
        const y = baselineY - barHeight
        const tooltipX = clamp(xCenter - TOOLTIP_WIDTH / 2, SVG_PADDING.left, SVG_WIDTH - SVG_PADDING.right - TOOLTIP_WIDTH)
        const tooltipY = clamp(y - TOOLTIP_HEIGHT - 12, 8, baselineY - TOOLTIP_HEIGHT - 8)

        return (
          <g pointerEvents="none">
            <rect
              x={tooltipX}
              y={tooltipY}
              width={TOOLTIP_WIDTH}
              height={TOOLTIP_HEIGHT}
              rx={8}
              fill="#ffffff"
              stroke="#dfe8e4"
              filter="drop-shadow(0 10px 18px rgba(15, 32, 26, 0.16))"
            />
            <text x={tooltipX + 12} y={tooltipY + 22} fill={TEXT_COLOR} fontSize={11} fontWeight={700}>
              {shortenLabel(row.label, 18)}
            </text>
            <text x={tooltipX + 12} y={tooltipY + 43} fill="#12211b" fontSize={14} fontWeight={800}>
              {valueFormatter(row.value)}
            </text>
          </g>
        )
      })() : null}
    </svg>
  )
}

const DonutDistributionChart = ({ rows }: { rows: ChartRow[] }) => {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null)
  const filteredRows = rows.filter((row) => row.value > 0)

  if (filteredRows.length === 0) {
    return <ChartEmptyState message="No status data available" />
  }

  const total = filteredRows.reduce((sum, row) => sum + row.value, 0)
  const radius = 74
  const centerX = SVG_WIDTH / 2
  const centerY = SVG_HEIGHT / 2 - 8
  const circumference = 2 * Math.PI * radius

  const segments = filteredRows.reduce<
    Array<{ row: ChartRow; color: string; length: number; offset: number }>
  >((acc, row, index) => {
    const consumed = acc.reduce((sum, segment) => sum + segment.length, 0)
    const length = (row.value / total) * circumference

    acc.push({
      row,
      color: CHART_COLORS[index % CHART_COLORS.length],
      length,
      offset: -consumed,
    })

    return acc
  }, [])

  return (
    <div style={{ height: '100%', display: 'grid', gridTemplateRows: '1fr auto' }}>
      <svg
        viewBox={`0 0 ${SVG_WIDTH} ${SVG_HEIGHT}`}
        width="100%"
        height="100%"
        role="img"
        aria-label="Order status donut chart"
      >
        <circle cx={centerX} cy={centerY} r={radius} fill="none" stroke="#edf3f2" strokeWidth={26} />

        <g transform={`rotate(-90 ${centerX} ${centerY})`}>
          {segments.map((segment, index) => (
            <circle
              key={`${segment.row.label}-${index}`}
              cx={centerX}
              cy={centerY}
              r={radius}
              fill="none"
              stroke={segment.color}
              strokeWidth={hoveredIndex === index ? 32 : 26}
              strokeLinecap="round"
              strokeDasharray={`${segment.length} ${circumference - segment.length}`}
              strokeDashoffset={segment.offset}
              opacity={hoveredIndex === null || hoveredIndex === index ? 1 : 0.32}
              style={{ transition: 'opacity 140ms ease, stroke-width 140ms ease' }}
              onMouseEnter={() => setHoveredIndex(index)}
              onMouseLeave={() => setHoveredIndex(null)}
              onFocus={() => setHoveredIndex(index)}
              onBlur={() => setHoveredIndex(null)}
              tabIndex={0}
              role="button"
              aria-label={`${segment.row.label}: ${formatNumber(segment.row.value)}`}
            />
          ))}
        </g>

        <circle cx={centerX} cy={centerY} r={44} fill="#ffffff" />

        <text x={centerX} y={centerY - 2} textAnchor="middle" fill="#12211b" fontSize={26} fontWeight={700}>
          {formatNumber(hoveredIndex !== null ? segments[hoveredIndex].row.value : total)}
        </text>
        <text x={centerX} y={centerY + 18} textAnchor="middle" fill={TEXT_COLOR} fontSize={12}>
          {hoveredIndex !== null ? shortenLabel(segments[hoveredIndex].row.label, 18) : 'Total Orders'}
        </text>
      </svg>

      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 10,
          justifyContent: 'center',
          paddingBottom: 4,
        }}
      >
        {segments.map((segment, index) => (
          <div
            key={`legend-${segment.row.label}-${index}`}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              fontSize: 12,
              color: hoveredIndex === index ? '#12211b' : '#334155',
              fontWeight: hoveredIndex === index ? 800 : 400,
            }}
            onMouseEnter={() => setHoveredIndex(index)}
            onMouseLeave={() => setHoveredIndex(null)}
          >
            <span
              style={{
                width: 10,
                height: 10,
                borderRadius: 999,
                backgroundColor: segment.color,
              }}
            />
            <span>{segment.row.label}</span>
          </div>
        ))}
      </div>
    </div>
  )
}

const DashboardPage = () => {
  const activeCurrency = useSellerCurrencyStore((state) => state.currency)
  const [data, setData] = useState<DashboardResponse | null>(null)
  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs] | null>([
    dayjs().subtract(29, 'day'),
    dayjs(),
  ])
  const [loading, setLoading] = useState(true)

  const disableFutureDate = (current: Dayjs) => current.isAfter(dayjs(), 'day')

  useEffect(() => {
    const params = dateRange
      ? {
          start_date: dateRange[0].format('YYYY-MM-DD'),
          end_date: dateRange[1].format('YYYY-MM-DD'),
        }
      : undefined

    setLoading(true)

    sellerDashboardAPI
      .stats<DashboardResponse>(params)
      .then((response) => {
        setData(response.data)
      })
      .catch(() => {
        setData(null)
      })
      .finally(() => {
        setLoading(false)
      })
  }, [dateRange])

  const revenueChartData = useMemo(
    () => normalizeSeriesChart(data?.analytics?.revenue_chart),
    [data?.analytics?.revenue_chart],
  )

  const ordersChartData = useMemo(
    () => normalizeSeriesChart(data?.analytics?.orders_chart),
    [data?.analytics?.orders_chart],
  )

  const inventoryChartData = useMemo(
    () => normalizeSeriesChart(data?.analytics?.inventory_bar),
    [data?.analytics?.inventory_bar],
  )

  const orderStatusData = useMemo(
    () => normalizeSeriesChart(data?.analytics?.order_status_pie),
    [data?.analytics?.order_status_pie],
  )

  const categorySalesData = useMemo(
    () => normalizeSeriesChart(data?.analytics?.category_sales),
    [data?.analytics?.category_sales],
  )

  const weekdayOrdersData = useMemo(
    () => normalizeSeriesChart(data?.analytics?.weekday_orders),
    [data?.analytics?.weekday_orders],
  )

  const topProductsUnitsData = useMemo(
    () => normalizeSeriesChart(data?.analytics?.top_products, 0),
    [data?.analytics?.top_products],
  )

  const topProductsRevenueData = useMemo(
    () => normalizeSeriesChart(data?.analytics?.top_products, 1),
    [data?.analytics?.top_products],
  )

  const orderValueBucketsData = useMemo(
    () => normalizeSeriesChart(data?.analytics?.order_value_buckets),
    [data?.analytics?.order_value_buckets],
  )

  const revenueTrendData = useMemo(
    () => normalizeSeriesChart(data?.analytics?.revenue_trend),
    [data?.analytics?.revenue_trend],
  )

  const totals = data?.totals ?? {}
  const totalProducts = Number(totals.total_products ?? 0)
  const approvedProducts = Number(totals.total_approved_products ?? 0)
  const totalOrders = Number(totals.total_orders ?? 0)
  const totalRevenue = Number(totals.total_revenue ?? 0)
  const totalEarnings = Number(totals.wallet_total_earnings ?? 0)
  const totalPayoutRequested = Number(totals.wallet_total_payout_requested ?? 0)

  const displayRevenueChartData = ensureSeriesHasVisibleSignal(
    revenueChartData,
    totalRevenue,
    'Current',
  )
  const displayOrdersChartData = ensureSeriesHasVisibleSignal(ordersChartData, totalOrders, 'Current')
  const displayInventoryChartData = ensureSeriesHasVisibleSignal(
    inventoryChartData,
    totalProducts,
    'Current',
  )
  const displayOrderStatusData = ensurePieHasVisibleSignal(orderStatusData, totalOrders)
  const displayCategorySalesData = ensureSeriesHasVisibleSignal(categorySalesData, totalRevenue, 'Current')
  const displayWeekdayOrdersData = ensureSeriesHasVisibleSignal(weekdayOrdersData, totalOrders, 'Current')
  const displayTopProductsUnitsData = ensureSeriesHasVisibleSignal(topProductsUnitsData, totalOrders, 'Current')
  const displayTopProductsRevenueData = ensureSeriesHasVisibleSignal(topProductsRevenueData, totalRevenue, 'Current')
  const displayOrderValueBucketsData = ensurePieHasVisibleSignal(orderValueBucketsData, totalOrders)
  const displayRevenueTrendData = ensureSeriesHasVisibleSignal(revenueTrendData, totalRevenue, 'Current')
  const hasAdvancedAnalytics = Boolean(data?.analytics_access?.advanced)
  const hasPersonalizedAnalytics = Boolean(data?.analytics_access?.personalized)
  const aiFeatureRoadmap = data?.feature_roadmap?.ai_features ?? []
  const dashboardAnalysis = data?.analysis
  const periodComparison = dashboardAnalysis?.period_comparison
  const weekdayAnalysis = dashboardAnalysis?.weekday_analysis
  const topProductAnalysis = dashboardAnalysis?.top_product_analysis
  const orderValueAnalysis = dashboardAnalysis?.order_value_analysis
  const inventoryAnalysis = dashboardAnalysis?.inventory_analysis
  const planUtilization = dashboardAnalysis?.plan_utilization
  const recommendations = dashboardAnalysis?.recommendations ?? []

  const approvalRate = totalProducts > 0 ? (approvedProducts / totalProducts) * 100 : 0
  const revenuePerOrder = totalOrders > 0 ? totalRevenue / totalOrders : 0
  const peakOrderPoint = displayOrdersChartData.reduce((peak, row) => Math.max(peak, row.value), 0)

  if (loading) {
    return (
      <div className="seller-loading-screen" style={{ minHeight: 320 }}>
        <Spin size="large" />
      </div>
    )
  }

  if (!data) {
    return (
      <Alert
        title="Failed to load dashboard data"
        description="Please refresh the page and try again."
        type="error"
      />
    )
  }

  return (
    <>
      <div className="seller-page-header">
        <div>
          <h1>Seller Dashboard</h1>
          <p className="seller-page-description">
            Track revenue momentum, order flow, approval ratio, and inventory health from one
            performance command center.
          </p>
        </div>
        <div className="seller-toolbar">
          <DatePicker.RangePicker
            className="seller-dashboard-date-range"
            classNames={{ popup: { root: 'seller-dashboard-date-range-popup' } }}
            value={dateRange}
            format="YYYY-MM-DD"
            allowClear
            inputReadOnly
            disabledDate={disableFutureDate}
            onChange={(dates) => {
              if (!dates) {
                setDateRange(null)
                return
              }

              if (dates[0] && dates[1]) {
                const today = dayjs()
                const start = dates[0].isAfter(today, 'day') ? today : dates[0]
                const end = dates[1].isAfter(today, 'day') ? today : dates[1]

                setDateRange(start.isAfter(end, 'day') ? [end, start] : [start, end])
              }
            }}
          />
        </div>
      </div>

      <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
        <Col xs={24} sm={12} xl={6}>
          <Card>
            <Statistic title="Total Products" value={totalProducts} prefix={<AppstoreOutlined />} />
            <Typography.Text type="secondary">Published and pending catalog entries</Typography.Text>
          </Card>
        </Col>
        <Col xs={24} sm={12} xl={6}>
          <Card>
            <Statistic title="Approved Products" value={approvedProducts} />
            <Space size="small" style={{ marginTop: 6 }}>
              <Tag color="green">{approvalRate.toFixed(1)}% approval rate</Tag>
            </Space>
          </Card>
        </Col>
        <Col xs={24} sm={12} xl={6}>
          <Card>
            <Statistic title="Total Orders" value={totalOrders} prefix={<ShoppingCartOutlined />} />
            <Typography.Text type="secondary">
              Peak window: {formatNumber(peakOrderPoint)} orders
            </Typography.Text>
          </Card>
        </Col>
        <Col xs={24} sm={12} xl={6}>
          <Card>
            <Statistic
              title={`Revenue (${activeCurrency})`}
              value={totalRevenue}
              formatter={(value) => formatCurrency(Number(value ?? 0))}
            />
            <Typography.Text type="secondary">
              Avg order value: {formatCurrency(revenuePerOrder)}
            </Typography.Text>
          </Card>
        </Col>
        <Col xs={24} sm={12} xl={6}>
          <Card>
            <Statistic
              title={`Wallet Total Earnings (${activeCurrency})`}
              value={totalEarnings}
              formatter={(value) => formatCurrency(Number(value ?? 0))}
            />
            <Typography.Text type="secondary">Cumulative credited earnings</Typography.Text>
          </Card>
        </Col>
        <Col xs={24} sm={12} xl={6}>
          <Card>
            <Statistic
              title={`Total Payout Requested (${activeCurrency})`}
              value={totalPayoutRequested}
              formatter={(value) => formatCurrency(Number(value ?? 0))}
            />
            <Typography.Text type="secondary">Submitted payout request amount</Typography.Text>
          </Card>
        </Col>
      </Row>

      <Row gutter={[20, 20]}>
        <Col xs={24} xl={16}>
          <Card title="Revenue Performance Trend">
            <div style={CHART_BOX_STYLE}>
              <TrendChart
                rows={displayRevenueChartData}
                stroke="#0f766e"
                fill="rgba(15, 118, 110, 0.16)"
                valueFormatter={formatCurrency}
              />
            </div>
          </Card>
        </Col>

        <Col xs={24} xl={8}>
          <Card title="Order Status Distribution">
            <div style={CHART_BOX_STYLE}>
              <DonutDistributionChart rows={displayOrderStatusData} />
            </div>
          </Card>
        </Col>
      </Row>

      <Row gutter={[20, 20]} style={{ marginTop: 4 }}>
        <Col xs={24} xl={12}>
          <Card title="Order Velocity">
            <div style={CHART_BOX_STYLE}>
              <TrendChart rows={displayOrdersChartData} stroke="#0ea5e9" valueFormatter={formatNumber} />
            </div>
          </Card>
        </Col>

        <Col xs={24} xl={12}>
          <Card title="Inventory Pressure Analysis">
            <div style={CHART_BOX_STYLE}>
              <BarDistributionChart rows={displayInventoryChartData} color="#0f766e" />
            </div>
          </Card>
        </Col>
      </Row>

      <Row gutter={[20, 20]} style={{ marginTop: 4 }}>
        <Col xs={24} lg={12}>
          <Card title="Weekday Demand Pattern">
            <div style={CHART_BOX_STYLE}>
              <BarDistributionChart rows={displayWeekdayOrdersData} color="#f59e0b" />
            </div>
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card title="Order Value Mix">
            <div style={CHART_BOX_STYLE}>
              <DonutDistributionChart rows={displayOrderValueBucketsData} />
            </div>
          </Card>
        </Col>
      </Row>

      {dashboardAnalysis ? (
        <Row gutter={[20, 20]} style={{ marginTop: 4 }}>
          <Col xs={24} xl={12}>
            <Card
              title="Period Comparison"
              extra={<Tag color={ANALYSIS_THEME.comparison.tag}>{formatPercent(periodComparison?.revenue_change_percent)}</Tag>}
              style={{ borderColor: ANALYSIS_THEME.comparison.border, background: ANALYSIS_THEME.comparison.background }}
            >
              <Row gutter={[12, 12]}>
                <Col xs={24} sm={12}>
                  <Card size="small">
                    <Typography.Text type="secondary">Revenue vs previous period</Typography.Text>
                    <div style={{ fontSize: 22, fontWeight: 800 }}>{formatCurrency(periodComparison?.current_revenue ?? totalRevenue)}</div>
                    <Typography.Text type="secondary">
                      Prev: {formatCurrency(periodComparison?.previous_revenue ?? 0)}
                    </Typography.Text>
                  </Card>
                </Col>
                <Col xs={24} sm={12}>
                  <Card size="small">
                    <Typography.Text type="secondary">Orders vs previous period</Typography.Text>
                    <div style={{ fontSize: 22, fontWeight: 800 }}>{periodComparison?.current_orders ?? totalOrders}</div>
                    <Typography.Text type="secondary">
                      Prev: {periodComparison?.previous_orders ?? 0}
                    </Typography.Text>
                  </Card>
                </Col>
                <Col xs={24} sm={12}>
                  <Card size="small">
                    <Typography.Text type="secondary">Average order value</Typography.Text>
                    <div style={{ fontSize: 22, fontWeight: 800 }}>{formatCurrency(periodComparison?.current_aov ?? revenuePerOrder)}</div>
                    <Typography.Text type="secondary">
                      Prev: {formatCurrency(periodComparison?.previous_aov ?? 0)}
                    </Typography.Text>
                  </Card>
                </Col>
                <Col xs={24} sm={12}>
                  <Card size="small">
                    <Typography.Text type="secondary">Change window</Typography.Text>
                    <div style={{ fontSize: 22, fontWeight: 800 }}>{periodComparison?.range_days ?? 7} days</div>
                    <Typography.Text type="secondary">
                      Orders change: {formatPercent(periodComparison?.orders_change_percent)}
                    </Typography.Text>
                  </Card>
                </Col>
              </Row>
            </Card>
          </Col>

          <Col xs={24} xl={12}>
            <Card
              title="Seller Focus Signals"
              style={{ borderColor: ANALYSIS_THEME.signals.border, background: ANALYSIS_THEME.signals.background }}
            >
              <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                <Alert
                  type={inventoryAnalysis?.status === 'critical' ? 'error' : inventoryAnalysis?.status === 'watch' ? 'warning' : 'success'}
                  showIcon
                  message={
                    inventoryAnalysis?.status === 'critical'
                      ? 'Inventory pressure is critical'
                      : inventoryAnalysis?.status === 'watch'
                        ? 'Inventory pressure needs review'
                        : 'Inventory is in a healthy range'
                  }
                  description={`${inventoryAnalysis?.low_stock_products ?? 0} products are low on stock across ${inventoryAnalysis?.total_products ?? totalProducts} products. Usage of plan capacity is ${formatPercent(inventoryAnalysis?.product_limit_usage_percent)}.`}
                />
                <Alert
                  type="info"
                  showIcon
                  message={`Peak demand day: ${weekdayAnalysis?.peak_day ?? 'N/A'}`}
                  description={`Orders on that day: ${weekdayAnalysis?.peak_orders ?? 0} (${formatPercent(weekdayAnalysis?.peak_share_percent)} of demand). Top product concentration: ${formatPercent(topProductAnalysis?.top_product_share)}.`}
                />
                <Alert
                  type="info"
                  showIcon
                  message={`Premium order mix: ${formatPercent(orderValueAnalysis?.premium_order_share)}`}
                  description={`Product plan usage: ${formatPercent(planUtilization?.product_usage_percent)}. Staff plan usage: ${formatPercent(planUtilization?.staff_usage_percent)}.`}
                />
              </Space>
            </Card>
          </Col>
        </Row>
      ) : null}

      {recommendations.length > 0 ? (
        <Row gutter={[20, 20]} style={{ marginTop: 4 }}>
          <Col xs={24}>
            <Card
              title="Seller Recommendations"
              style={{ borderColor: ANALYSIS_THEME.recommendations.border, background: ANALYSIS_THEME.recommendations.background }}
            >
              <Row gutter={[16, 16]}>
                {recommendations.map((item, index) => (
                  <Col xs={24} md={12} xl={8} key={`${item.title ?? 'recommendation'}-${index}`}>
                    <Card size="small" style={{ minHeight: 150, borderColor: '#e2e8f0' }}>
                      <Space direction="vertical" size={8} style={{ width: '100%' }}>
                        <Tag color={item.severity === 'error' ? 'red' : item.severity === 'warning' ? 'orange' : item.severity === 'success' ? 'green' : 'blue'}>
                          {item.severity ?? 'info'}
                        </Tag>
                        <Typography.Title level={5} style={{ margin: 0 }}>{item.title ?? 'Recommendation'}</Typography.Title>
                        <Typography.Paragraph style={{ marginBottom: 0 }}>{item.message ?? 'Review this seller signal.'}</Typography.Paragraph>
                      </Space>
                    </Card>
                  </Col>
                ))}
              </Row>
            </Card>
          </Col>
        </Row>
      ) : null}

      {hasAdvancedAnalytics ? (
        <Row gutter={[20, 20]} style={{ marginTop: 4 }}>
          <Col xs={24} xl={12}>
            <Card
              title="Revenue Trend with Moving Average"
              style={{ borderColor: ANALYSIS_THEME.revenueTrend.border, background: ANALYSIS_THEME.revenueTrend.background }}
            >
              <div style={CHART_BOX_STYLE}>
                <TrendChart rows={displayRevenueTrendData} stroke="#0f766e" fill="rgba(15, 118, 110, 0.16)" valueFormatter={formatCurrency} />
              </div>
            </Card>
          </Col>

          <Col xs={24} xl={12}>
            <Card
              title="Top Products by Units Sold"
              style={{ borderColor: ANALYSIS_THEME.topProducts.border, background: ANALYSIS_THEME.topProducts.background }}
            >
              <div style={CHART_BOX_STYLE}>
                <BarDistributionChart rows={displayTopProductsUnitsData} color="#6366f1" />
              </div>
            </Card>
          </Col>

          <Col xs={24} xl={12} style={{ marginTop: 4 }}>
            <Card
              title="Top Products Revenue"
              style={{ borderColor: ANALYSIS_THEME.topProducts.border, background: ANALYSIS_THEME.topProducts.background }}
            >
              <div style={CHART_BOX_STYLE}>
                <BarDistributionChart rows={displayTopProductsRevenueData} color="#8b5cf6" valueFormatter={formatCurrency} />
              </div>
            </Card>
          </Col>

          <Col xs={24} xl={12} style={{ marginTop: 4 }}>
            <Card
              title={`Advanced Analytics${data.analytics_access?.personalized ? ' - Personalized' : ''}`}
              extra={<Tag color={data.analytics_access?.personalized ? 'purple' : 'blue'}>{data.analytics_access?.level ?? 'advanced'}</Tag>}
              style={{ borderColor: ANALYSIS_THEME.categorySales.border, background: ANALYSIS_THEME.categorySales.background }}
            >
              <div style={CHART_BOX_STYLE}>
                <BarDistributionChart rows={displayCategorySalesData} color="#9333ea" />
              </div>
            </Card>
          </Col>
        </Row>
      ) : null}

      {hasPersonalizedAnalytics && Array.isArray(data?.analytics?.personalized_insights) && data.analytics.personalized_insights.length > 0 ? (
        <Row gutter={[20, 20]} style={{ marginTop: 4 }}>
          <Col xs={24}>
            <Card title="Personalized Insights">
              <Row gutter={[16, 16]}>
                {data.analytics.personalized_insights.map((insight, index) => (
                  <Col xs={24} md={12} xl={8} key={`${insight.title ?? 'insight'}-${index}`}>
                    <Card size="small" style={{ borderColor: '#dbeafe', background: '#f8fbff', minHeight: 140 }}>
                      <Space direction="vertical" size={8} style={{ width: '100%' }}>
                        <Tag color={insight.type === 'inventory' ? 'orange' : insight.type === 'revenue' ? 'green' : 'blue'}>
                          {insight.type ?? 'insight'}
                        </Tag>
                        <Typography.Title level={5} style={{ margin: 0 }}>
                          {insight.title ?? 'Insight'}
                        </Typography.Title>
                        <Typography.Paragraph style={{ marginBottom: 0 }}>
                          {insight.message ?? 'A personalized insight is available.'}
                        </Typography.Paragraph>
                      </Space>
                    </Card>
                  </Col>
                ))}
              </Row>
            </Card>
          </Col>
        </Row>
      ) : null}

      {aiFeatureRoadmap.length > 0 ? (
        <Row gutter={[20, 20]} style={{ marginTop: 4 }}>
          <Col xs={24}>
            <Card title="AI Features Roadmap" extra={<Tag color="gold">Coming soon</Tag>}>
              <Row gutter={[16, 16]}>
                {aiFeatureRoadmap.map((feature) => (
                  <Col xs={24} md={12} xl={8} key={feature.code}>
                    <Card size="small" style={{ borderColor: '#fde68a', background: 'linear-gradient(180deg, rgba(250, 204, 21, 0.10) 0%, #ffffff 40%)', minHeight: 160 }}>
                      <Space direction="vertical" size={8} style={{ width: '100%' }}>
                        <Tag color="gold">Coming soon</Tag>
                        <Typography.Title level={5} style={{ margin: 0 }}>{feature.name}</Typography.Title>
                        <Typography.Paragraph style={{ marginBottom: 0, color: '#475569' }}>{feature.description}</Typography.Paragraph>
                      </Space>
                    </Card>
                  </Col>
                ))}
              </Row>
            </Card>
          </Col>
        </Row>
      ) : null}
    </>
  )
}

export default DashboardPage
